/**
 * SellerLocatorPlan — the honest stand-in for the live 2GIS map.
 *
 * WHY THIS EXISTS
 * The finder ships without a MapGL key in every environment that has not been
 * given one, and the SDK can also fail to load on a bazaar 3G connection. That
 * fallback is a REAL shipped state, not a dev artefact, so it gets designed
 * like one. What it replaced — `@garden/ui`'s `MapPlaceholder` — drew a beige
 * illustration of invented streets and blocks: fake cartography that reads as
 * a broken map and quietly implies geography the product does not have.
 *
 * This draws a PLAN instead, in the product's own counter-slip language: the
 * sunken ground, a grid of DRAWN rules (RAISE: visible armature), the shyrdak
 * motif at real presence rather than 3% opacity, and one numbered pin per
 * seller. It is captioned as approximate, in words, on the surface itself —
 * so it can never be mistaken for a street map.
 *
 * HONESTY RULE: only sellers that actually carry coordinates are plotted.
 * `map-projection.ts` will happily place a coordinate-less seller on a hashed
 * grid cell (a stable but INVENTED position); on a surface a buyer might
 * navigate by, that is a lie, so the parent filters those out and the plan
 * reports how many sellers it could not place.
 *
 * The pin number is the ledger row number — hovering either one lights the
 * other, which is the whole point of showing them side by side.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { SupportedLanguage } from "@/lib/i18n";
import type { SellerSummary } from "@/lib/garden-api";

import { sellerStallCode } from "@/features/shops/SellerRow";
import {
  buildSellerPins,
  type SellerMapPin,
} from "@/features/shops/map-projection";

/** A projected pin plus the ledger row number that keys it. */
export interface LocatorPoint extends SellerMapPin {
  /** 1-based ledger position — printed on the pin AND in the row gutter. */
  index: number;
}

/** What the plan can draw, plus what it honestly could not place. */
export interface LocatorLayout {
  points: LocatorPoint[];
  unplacedCount: number;
}

/**
 * Fan-out radius (in % of the plan box) for pins that project onto the same
 * spot. Real markets do this constantly: every trader in рынок Уста is
 * registered at the market's own coordinate pair, so two sellers arrive with
 * byte-identical lat/lng and would otherwise stack into one unclickable pin.
 */
const CLUSTER_RADIUS = 4.5;
/** Keep fanned pins inside the plan box (mirrors map-projection's inset). */
const PLAN_INSET = 8;

function clampToPlan(value: number): number {
  return Math.min(100 - PLAN_INSET, Math.max(PLAN_INSET, value));
}

/**
 * Build the plan layout for a ledger page.
 *
 * Only sellers carrying real coordinates are placed — `projectSeller`'s hashed
 * grid fallback is deliberately NOT used here (see the header note: a made-up
 * position on a locator surface is a lie). Co-located sellers are fanned onto
 * a small deterministic circle so each stays individually clickable.
 */
export function buildLocatorPoints(sellers: SellerSummary[]): LocatorLayout {
  const placed = sellers
    .map((seller, i) => ({ seller, index: i + 1 }))
    .filter(({ seller }) => seller.lat !== null && seller.lng !== null);

  const pins = buildSellerPins(placed.map((entry) => entry.seller));
  const points: LocatorPoint[] = pins.map((pin, i) => ({
    ...pin,
    index: placed[i]!.index,
  }));

  // Group by rounded position, then fan any group with more than one member.
  const groups = new Map<string, LocatorPoint[]>();
  for (const point of points) {
    const key = `${point.x.toFixed(1)}:${point.y.toFixed(1)}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(point);
    else groups.set(key, [point]);
  }
  for (const bucket of groups.values()) {
    if (bucket.length < 2) continue;
    bucket.forEach((point, i) => {
      const angle = (2 * Math.PI * i) / bucket.length - Math.PI / 2;
      point.x = clampToPlan(point.x + Math.cos(angle) * CLUSTER_RADIUS);
      point.y = clampToPlan(point.y + Math.sin(angle) * CLUSTER_RADIUS);
    });
  }

  return { points, unplacedCount: sellers.length - placed.length };
}

export interface SellerLocatorPlanProps {
  points: LocatorPoint[];
  /** Sellers the plan could not place (no coordinates on the wire). */
  unplacedCount: number;
  height: number | string;
  selectedSellerId?: string | null;
  hoveredSellerId?: string | null;
  onSelect: (seller: SellerSummary) => void;
  onHover?: (sellerId: string | null) => void;
  lang: SupportedLanguage;
}

/** Grid-rule positions, in percent. A measured plan, not a texture. */
const RULES = [20, 40, 60, 80] as const;

export function SellerLocatorPlan({
  points,
  unplacedCount,
  height,
  selectedSellerId,
  hoveredSellerId,
  onSelect,
  onHover,
  lang,
}: SellerLocatorPlanProps): ReactElement {
  const { t } = useTranslation("shops");

  return (
    <div
      data-testid="shops-locator-plan"
      style={{ height }}
      className="relative w-full overflow-hidden bg-[var(--g-sunken)]"
    >
      {/*
       * NO MOTIF HERE — deliberately.
       *
       * The shyrdak lattice is the product's ORNAMENT. Spread across the plan
       * field with pins dropped on it, it stops being ornament and starts
       * reading as terrain: a textured ground with markers on it is a map, and
       * this product has no map to show when MapGL is absent. The pins are
       * projected from real coordinates, but the field behind them means
       * nothing, and an ornament impersonating data is the one kind of faking
       * this design language refuses outright.
       *
       * What stays is the drawn grid below: an obvious schematic that reads as
       * a diagram, never as geography.
       */}

      {/* Drawn armature — the rules ARE the plan, not implied whitespace. */}
      <svg
        aria-hidden="true"
        focusable="false"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        className="absolute inset-0 h-full w-full text-[var(--g-rule)]"
      >
        {RULES.map((pos) => (
          <line
            key={`v${pos}`}
            x1={pos}
            y1="0"
            x2={pos}
            y2="100"
            stroke="currentColor"
            strokeWidth="0.25"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {RULES.map((pos) => (
          <line
            key={`h${pos}`}
            x1="0"
            y1={pos}
            x2="100"
            y2={pos}
            stroke="currentColor"
            strokeWidth="0.25"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>

      {points.length === 0 ? (
        <p className="absolute inset-0 grid place-items-center px-6 text-center text-[13px] text-[var(--g-ink-2)]">
          {t("plan.empty")}
        </p>
      ) : null}

      {points.map((point) => {
        const seller = point.seller;
        const isSelected = seller.id === selectedSellerId;
        const isHovered = seller.id === hoveredSellerId;
        const isLive = isSelected || isHovered;
        return (
          <button
            key={seller.id}
            type="button"
            data-testid="shops-plan-pin"
            data-seller-id={seller.id}
            data-active={isLive ? "true" : "false"}
            onClick={() => onSelect(seller)}
            onMouseEnter={() => onHover?.(seller.id)}
            onMouseLeave={() => onHover?.(null)}
            onFocus={() => onHover?.(seller.id)}
            onBlur={() => onHover?.(null)}
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
            className="absolute grid size-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-sunken)]"
          >
            {/* The fill stays `--g-primary` in every state so the number keeps
                its ~7:1 contrast; terracotta marks attention as a ring, which
                is what the direction reserves the accent for. */}
            <span
              className={[
                "grid size-7 place-items-center rounded-full bg-[var(--g-primary)]",
                "transition-[transform,box-shadow] duration-150 ease-[var(--g-ease)]",
                isLive
                  ? "scale-110 shadow-[0_0_0_3px_var(--g-accent)]"
                  : "shadow-[var(--g-shadow-sm)]",
              ].join(" ")}
            >
              <span className="g-data text-[11px] font-bold leading-none text-[var(--g-on-primary)]">
                {String(point.index).padStart(2, "0")}
              </span>
            </span>
            {/* The label only appears for the live pin — ten permanent labels
                would be noise, and the ledger already carries every name. */}
            <span
              className={[
                "pointer-events-none absolute left-1/2 top-full -translate-x-1/2 whitespace-nowrap rounded-[var(--g-radius-sm)] border border-[var(--g-rule)] bg-[var(--g-surface)] px-1.5 py-0.5 text-[11px] shadow-[var(--g-shadow-sm)] transition-opacity duration-150",
                isLive ? "opacity-100" : "opacity-0",
              ].join(" ")}
            >
              <span className="g-data text-[var(--g-ink-2)]">
                {sellerStallCode(seller, lang)}
              </span>
            </span>
            <span className="sr-only">{seller.business_name}</span>
          </button>
        );
      })}

      {/* Caption: says what this is, in words, on the surface. */}
      <p className="absolute inset-x-0 bottom-0 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-t border-[var(--g-rule)] bg-[color-mix(in_srgb,var(--g-surface)_90%,transparent)] px-3 py-1.5 text-[11px] leading-tight text-[var(--g-ink-2)]">
        <span>{t("plan.hint")}</span>
        <span className="g-data">
          {t("plan.points", { count: points.length })}
          {unplacedCount > 0
            ? ` · ${t("plan.offPlan", { count: unplacedCount })}`
            : ""}
        </span>
      </p>
    </div>
  );
}
